import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { navigate } from '@/domain/router/redux/reduxActions';
import { isEqual } from 'lodash';
import {
  eventsListSelector,
  nextNumPageSelector,
  getEventsAction,
  getCountOfNewEventsAction,
  newEventsCounterSelector,
  toggleEventPanelAction,
  isOpenedSelector,
  exportHistoryEventsAction,
} from '@/domain/events';
import { AP_TRAIL_FILTERS_INITIAL, fetchApTrailFilterOptions, toApFilterPayload } from '@/domain/events/apFilters';
import { documentHideLinkedDocs, documentLinkedOpenStatusSelector, setLinkingTagAction } from '@/domain/documents';
import { saveBackSearchUrlAction } from '@/domain/env';
import { querySelector } from '@/domain/router';
import {
  chatAllUsersByIdSelector,
  setActiveChannelAction,
  setActiveChannelForceAction,
  setOpenAction,
} from '@/domain/chat';
import ROUTES_PATH from '@/router/routesPathConfig';
import { generatePath } from 'react-router-dom';
import { bodyToggler } from '@/lib/domHelpers';
import { promisify, simulateMouseClick } from '@/lib/helpers';
import makeCancelable from '@/lib/makeCancelable';
import {
  getDocumentUrl,
  getPreviewSrc,
  getRotationAngle,
} from '@/pages/company/WorkspaceRoute/components/workSpace/helpers';
import { formatDate } from '@/lib/dateTime';
import { showApiErrorToast } from '@/services/api';
import { hotkeyMatcher } from '@/lib/hotkeysHelpers';

import EventsHistoryDrawer, {
  EVENTS_HISTORY_DRAWER_WIDTH,
} from '@/components/mui/Layouts/components/EventsHistoryPanel/components/EventsHistoryDrawer';
import EventsHistoryFiltersBlock from '@/components/mui/Layouts/components/EventsHistoryPanel/components/FiltersBlock';
import EventsHistoryList from '@/components/mui/Layouts/components/EventsHistoryPanel/components/EventsList';
import DocumentPreview from '@/pages/company/WorkspaceRoute/components/DocumentPreview';
import { workspaceHotkeys } from '@/pages/company/WorkspaceRoute/components/DialogKeyboardHotkeys/hotkeys';

function filter(pattern) {
  return (f) => {
    if (f.link && f.link.type === 'linked_document') {
      // $FlowFixMe
      return f.link.confidential === pattern;
    }
    return true;
  };
}

class EventsHistoryPanel extends React.Component {
  _event;

  cancelable;

  constructor(props) {
    super(props);

    this.state = {
      preview: null,
      filters: AP_TRAIL_FILTERS_INITIAL,
      timestampFrom: null,
      timestampTo: null,
      initialSearchTypes: [],
      filterOptions: { users: [], types: [] },
      isFiltersBusy: true,
    };
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeydown);
    if (this.props.isOpened) this.loadFilterOptions();
  }

  componentDidUpdate(prevProps) {
    const { isOpened, getCountOfNewEvents } = this.props;
    if (isOpened && isOpened !== prevProps.isOpened) {
      getCountOfNewEvents();
      this.loadFilterOptions();
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    if (this.cancelable) {
      this.cancelable.cancel();
    }
    window.removeEventListener('keydown', this.handleKeydown);
  }

  handleShortcuts = (keys) => {
    const { toggleEventPanel, hideLinkedDocs, setLinkingTag, isOpenLinkedPanel } = this.props;
    if (isEqual(keys.toJS(), ['Control', 'KeyE'])) {
      // we need to close all modals and dropdowns
      const modalElement =
        document.getElementsByClassName('MuiModal-root')[0]?.getElementsByClassName('MuiBackdrop-root')[0] ||
        document.getElementById('root');

      simulateMouseClick(modalElement);

      if (isOpenLinkedPanel) {
        hideLinkedDocs();
        setLinkingTag('');
      }
      this.setState({ initialSearchTypes: ['upload_existing_document'] });
      toggleEventPanel();
    }
  };

  handleKeydown = (e) => {
    const isMatch = hotkeyMatcher(e, workspaceHotkeys, this.handleShortcuts);
    if (isMatch) {
      e.preventDefault();
    }
  };

  get documentItem() {
    const { preview } = this.state;
    const { documentsById } = this.props;
    // $FlowFixMe
    return documentsById.get(preview);
  }

  get dateFormat() {
    const { company } = this.props;
    if (company && typeof company.dateFormat === 'string') return company.dateFormat;
    return 'DD/MM/YYYY';
  }

  get items() {
    const { list, isConfidential } = this.props;
    return list.filter(filter(isConfidential)).toArray();
  }

  /** @param {any} entry */
  getAvatarSrc = (entry) => this.props.usersById.getIn([entry.username, 'picture']);

  loadFilterOptions = async () => {
    this.setState({ isFiltersBusy: true, filterOptions: { users: [], types: [] } });
    try {
      const filterOptions = await fetchApTrailFilterOptions();
      if (!this.isUnmounted) this.setState({ filterOptions });
    } catch (err) {
      showApiErrorToast(err);
    } finally {
      if (!this.isUnmounted) this.setState({ isFiltersBusy: false });
    }
  };

  get preview() {
    if (this._event) {
      return this._event;
    }
    throw new TypeError('Incorrect preview event type');
  }

  set preview(event) {
    this._event = event;
    this.handlePreview(event.id);
  }

  get getDocumentUrl() {
    const { match } = this.props;
    const { documentID } = this.preview.link;
    return getDocumentUrl(match.params.companyId, documentID);
  }

  get getPreviewSrc() {
    const { documentID } = this.preview.link;
    const { company } = this.props;
    return getPreviewSrc(company.id, documentID, true, true, true);
  }

  toggleOpen = () => {
    const { isOpened, getCountOfNewEvents, toggleEventPanel } = this.props;

    if (!isOpened) getCountOfNewEvents();

    toggleEventPanel();

    return new Promise((resolve) => {
      this.setState(
        {
          preview: null,
          timestampFrom: null,
          timestampTo: null,
          initialSearchTypes: [],
          filters: AP_TRAIL_FILTERS_INITIAL,
        },
        () => {
          bodyToggler(isOpened);
          resolve();
        },
      );
    });
  };

  handlePreview = (eventID) => {
    this.setState({
      preview: eventID,
    });
  };

  handleOpenDocument = () => {
    const { setNavigation } = this.props;
    const { preview } = this.state;

    this.toggleOpen();
    if (preview) {
      setNavigation(preview, false, true);
    }
  };

  handleEventAction = async (d) => {
    const {
      match,
      query,
      navigatePush,
      onShowLinked,
      setActiveChannel,
      setOpenChat,
      saveBackSearchUrl,
      setActiveChannelForce,
    } = this.props;

    const { link } = d;
    switch (link.type) {
      case 'document':
        this.preview = d;
        break;
      case 'linked_document':
        await this.toggleOpen();
        navigatePush(
          generatePath(ROUTES_PATH.COMPANY_WORKSPACE.absolute, {
            companyId: match.params.companyId,
            category1: link.rootCategory,
          }),
        );

        setTimeout(() => {
          onShowLinked(link.linkID);
        }, 500);

        break;
      case 'company_chat':
        await this.toggleOpen();
        setOpenChat(true);
        setActiveChannel({
          threadId: link.channel_name,
          companyId: link.companyID,
        });
        break;
      case 'document_chat':
        await this.toggleOpen();
        saveBackSearchUrl({ params: match.params, query });
        setActiveChannelForce({
          threadId: link.channel_name,
          companyId: link.companyID,
        });
        navigatePush(
          generatePath(ROUTES_PATH.DOCUMENT.absolute, {
            companyId: link.companyID,
            documentId: link.documentID,
          }),
        );
        break;
      default:
        break;
    }
  };

  handleLoadEvents = (nextNumPage, clear) => {
    const { getEvents } = this.props;
    const { timestampFrom, timestampTo, filters, initialSearchTypes } = this.state;

    if (!initialSearchTypes.length) {
      this.cancelable = makeCancelable(
        promisify(getEvents, {
          maxResult: 10,
          page: nextNumPage,
          timestampFrom: formatDate(timestampFrom),
          timestampTo: formatDate(timestampTo),
          ...toApFilterPayload(filters),
          clear,
        }),
      );
    }
  };

  handleGetNextPage = () => {
    const { nextNumPage } = this.props;
    this.handleLoadEvents(nextNumPage, false);
  };

  handleSetDate = (from, to) => {
    this.setState(
      {
        timestampFrom: from,
        timestampTo: to,
      },
      () => {
        this.handleLoadEvents(null, true);
      },
    );
  };

  handleExportEvents = () => {
    const { timestampFrom, timestampTo, filters } = this.state;
    const {
      exportHistoryEvents,
      company: { id },
    } = this.props;

    exportHistoryEvents({
      companyId: id,
      timestampFrom: formatDate(timestampFrom),
      timestampTo: formatDate(timestampTo),
      ...toApFilterPayload(filters),
    });
  };

  onChangeFilters = (filters) => {
    this.setState({ filters, initialSearchTypes: [] }, () => {
      this.handleLoadEvents(null, true);
    });
  };

  render() {
    const { list, isOpened, nextNumPage, counter } = this.props;
    const { preview, initialSearchTypes, filterOptions, isFiltersBusy } = this.state;
    const { items } = this;

    return (
      <EventsHistoryDrawer toggleOpen={this.toggleOpen} isOpened={isOpened}>
        <EventsHistoryFiltersBlock
          users={filterOptions.users}
          types={filterOptions.types}
          isBusy={isFiltersBusy}
          onChangeFilters={this.onChangeFilters}
          onChangeDate={this.handleSetDate}
          onClickExport={this.handleExportEvents}
          dateFormat={this.dateFormat}
          isDisabledExport={items.length === 0}
          initialSearchTypes={initialSearchTypes}
        />

        <EventsHistoryList
          items={items}
          getAvatarSrc={this.getAvatarSrc}
          onAction={this.handleEventAction}
          getNextEvents={this.handleGetNextPage}
          isNext={nextNumPage}
          onReload={() => this.handleLoadEvents(null, false)}
          counter={counter}
          dateFormat={this.dateFormat}
        />

        {typeof preview === 'string' && list.size ? (
          <DocumentPreview
            documentID={preview}
            src={this.getPreviewSrc}
            to={this.getDocumentUrl}
            rotationAngle={getRotationAngle(this.documentItem)}
            onClick={this.handleOpenDocument}
            close={() => this.handlePreview(null)}
            disabled={!this.preview.getIn(['link', 'exist'], true)}
            xGap={EVENTS_HISTORY_DRAWER_WIDTH}
          />
        ) : null}
      </EventsHistoryDrawer>
    );
  }
}

const mapStateToProps = (state) => ({
  list: eventsListSelector(state),
  nextNumPage: nextNumPageSelector(state),
  counter: newEventsCounterSelector(state),
  isOpened: isOpenedSelector(state),
  usersById: chatAllUsersByIdSelector(state),
  query: querySelector(state),
  isOpenLinkedPanel: documentLinkedOpenStatusSelector(state),
});

const mapDispatchToProps = {
  toggleEventPanel: toggleEventPanelAction,
  getEvents: getEventsAction,
  setActiveChannel: setActiveChannelAction,
  setActiveChannelForce: setActiveChannelForceAction,
  setOpenChat: setOpenAction,
  getCountOfNewEvents: getCountOfNewEventsAction,
  navigatePush: navigate.push,
  saveBackSearchUrl: saveBackSearchUrlAction,
  exportHistoryEvents: exportHistoryEventsAction,
  hideLinkedDocs: documentHideLinkedDocs,
  setLinkingTag: setLinkingTagAction,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(EventsHistoryPanel);
